import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Clock, User, ArrowLeft, ArrowRight } from 'lucide-react';
import blogPosts from '../data/blogData';
import SEO from '../components/SEO';

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.id === slug);
  const currentIndex = blogPosts.findIndex(p => p.id === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-[#2d2d51] mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-8">The article you're looking for doesn't exist.</p>
          <Link to="/resources/blogs" className="text-lime font-semibold hover:underline">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const prevPost = currentIndex > 0 ? blogPosts[currentIndex - 1] : null;
  const nextPost = currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null;

  const renderContent = (content) => {
    return content.split('\n').map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return null;
      if (trimmed.startsWith('## ')) {
        return <h2 key={i} className="font-heading text-xl sm:text-2xl font-bold text-[#2d2d51] mt-10 mb-4">{trimmed.replace('## ', '')}</h2>;
      }
      if (trimmed.startsWith('### ')) {
        return <h3 key={i} className="font-heading text-lg sm:text-xl font-bold text-[#2d2d51] mt-6 mb-3">{trimmed.replace('### ', '')}</h3>;
      }
      if (trimmed.startsWith('- **')) {
        const match = trimmed.match(/^- \*\*(.+?)\*\*(.*)$/);
        if (match) {
          return (
            <li key={i} className="text-gray-600 leading-relaxed ml-6 mb-2 list-disc">
              <strong className="text-[#2d2d51]">{match[1]}</strong>{match[2]}
            </li>
          );
        }
      }
      if (trimmed.startsWith('- ')) {
        return <li key={i} className="text-gray-600 leading-relaxed ml-6 mb-2 list-disc">{trimmed.replace('- ', '')}</li>;
      }
      if (trimmed.startsWith('**Q:')) {
        return <p key={i} className="font-bold text-[#2d2d51] mt-4 mb-1">{trimmed.replace(/\*\*/g, '')}</p>;
      }
      if (trimmed.match(/^\d+\.\s\*\*/)) {
        const match = trimmed.match(/^(\d+\.)\s\*\*(.+?)\*\*(.*)$/);
        if (match) {
          return (
            <p key={i} className="text-gray-600 leading-relaxed mb-2 ml-4">
              <span className="font-semibold text-[#2d2d51]">{match[1]} {match[2]}</span>{match[3]}
            </p>
          );
        }
      }
      return <p key={i} className="text-gray-600 leading-relaxed mb-4">{trimmed}</p>;
    });
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden" data-testid="blog-post-page">
      <SEO title={post.title} description={post.excerpt || post.title} path={`/resources/blogs/${post.id}`} />
      {/* Hero */}
      <section className="relative bg-[#2d2d51] overflow-hidden text-white py-12 md:py-16">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Link to="/resources/blogs" className="inline-flex items-center text-lime-300 hover:text-white mb-5 md:mb-6 transition-colors text-sm" data-testid="back-to-blog">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Link>
          <div className="mb-4">
            <span className="bg-lime/20 text-lime-300 text-xs sm:text-sm font-semibold px-4 py-1.5 rounded-full border border-lime/30">
              {post.category}
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold mb-5 md:mb-6 leading-tight">
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs sm:text-sm text-lime-300">
            <span className="flex items-center gap-2">
              <User className="h-4 w-4" />
              {post.author}
            </span>
            <span>{post.date}</span>
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {post.readTime}
            </span>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-2">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-52 sm:h-64 md:h-96 object-cover rounded-2xl shadow-2xl"
        />
      </div>

      {/* Content */}
      <article className="py-10 md:py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {renderContent(post.content)}
        </div>
      </article>

      {/* Navigation */}
      <section className="border-t border-slate-200 py-6 md:py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex justify-between items-center gap-3">
            {prevPost ? (
              <Link to={`/resources/blogs/${prevPost.id}`} className="group flex items-center gap-3 text-left" data-testid="prev-post">
                <ArrowLeft className="h-5 w-5 text-gray-400 group-hover:text-lime transition-colors" />
                <div>
                  <div className="text-xs text-gray-500 mb-1">Previous Article</div>
                  <div className="text-sm font-semibold text-[#2d2d51] group-hover:text-lime transition-colors max-w-xs truncate">{prevPost.title}</div>
                </div>
              </Link>
            ) : <div />}
            {nextPost ? (
              <Link to={`/resources/blogs/${nextPost.id}`} className="group flex items-center gap-3 text-right" data-testid="next-post">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Next Article</div>
                  <div className="text-sm font-semibold text-[#2d2d51] group-hover:text-lime transition-colors max-w-xs truncate">{nextPost.title}</div>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-lime transition-colors" />
              </Link>
            ) : <div />}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
